// Configurações → Integrações: webhooks dos cards (vários destinos), chaves da API de consulta e documentação.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import HistoryIcon from "@material-ui/icons/History";
import SendIcon from "@material-ui/icons/Send";
import MenuBookIcon from "@material-ui/icons/MenuBook";

import suporteApi, { mensagemErro } from "./api";
import { copiar, dataBR } from "./comum";
import IntegracoesSaida from "./IntegracoesSaida";

const mono = { fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12.5 };
const bloco = { ...mono, whiteSpace: "pre-wrap", background: "rgba(0,0,0,.05)", padding: 10, borderRadius: 6, margin: "6px 0", overflowX: "auto" };
const okStatus = (s) => s >= 200 && s < 300;
// manual público (pode ser passado a quem vai integrar): todos os eventos, objetos e endpoints
const MANUAL = "https://hub.allarmi.com.br/static/integracao.html";

const Status = ({ w }) => {
  if (!w.ultimo_envio_em) return <Typography variant="caption" color="textSecondary">sem envios ainda</Typography>;
  return (
    <Tooltip title={w.ultimo_erro || ""}>
      <Chip size="small" label={`${w.ultimo_status || "falhou"} · ${dataBR(w.ultimo_envio_em)}`}
        style={{ background: okStatus(w.ultimo_status) ? "#e8f5e9" : "#ffebee", color: okStatus(w.ultimo_status) ? "#2e7d32" : "#c62828" }} />
    </Tooltip>
  );
};

const SegredoMostrado = ({ titulo, valor, aviso, onClose }) => (
  <Dialog open={!!valor} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{titulo}</DialogTitle>
    <DialogContent>
      <Typography variant="body2" gutterBottom>{aviso}</Typography>
      <div style={{ ...bloco, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, wordBreak: "break-all" }}>{valor}</span>
        <IconButton size="small" onClick={() => copiar(valor)}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
      </div>
    </DialogContent>
    <DialogActions><Button color="primary" variant="contained" onClick={onClose}>Já guardei</Button></DialogActions>
  </Dialog>
);

const EditorWebhook = ({ w, eventos, onClose, onSalvo }) => {
  const [f, setF] = useState(null);
  useEffect(() => { if (w) setF({ nome: w.nome || "", url: w.url || "", eventos: w.eventos || [], ativo: w.ativo ?? true, incluir_filas: !!w.incluir_filas }); }, [w]);
  if (!w || !f) return null;
  const salvar = async () => {
    try {
      const { data } = w.id ? await suporteApi.put(`/api/integracoes/webhooks/${w.id}`, f) : await suporteApi.post("/api/integracoes/webhooks", f);
      onSalvo(data, !w.id);
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };
  const marcar = (ev, sim) => setF({ ...f, eventos: sim ? [...f.eventos, ev] : f.eventos.filter((x) => x !== ev) });
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{w.id ? "Editar webhook" : "Novo webhook"}</DialogTitle>
      <DialogContent>
        <TextField id="wh-nome" label="Nome" fullWidth size="small" variant="outlined" margin="dense" placeholder="Ex.: ERP, BI, n8n"
          value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} />
        <TextField id="wh-url" label="URL que recebe (POST)" fullWidth size="small" variant="outlined" margin="dense"
          placeholder="https://seu-sistema.com.br/webhooks/allarmi" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
        <Typography variant="subtitle2" style={{ marginTop: 10 }}>Eventos</Typography>
        <Typography variant="caption" color="textSecondary" component="div">Nenhum marcado = recebe todos.</Typography>
        {Object.entries(eventos).map(([ev, desc]) => (
          <FormControlLabel key={ev} style={{ display: "flex" }}
            control={<Checkbox size="small" color="primary" checked={f.eventos.includes(ev)} onChange={(e) => marcar(ev, e.target.checked)} />}
            label={<span><b style={mono}>{ev}</b>{` — ${desc}`}</span>} />
        ))}
        <FormControlLabel control={<Switch color="primary" checked={f.incluir_filas} onChange={(e) => setF({ ...f, incluir_filas: e.target.checked })} />}
          label="Incluir cards de outras filas (ex.: Contratação)" />
        <FormControlLabel control={<Switch color="primary" checked={f.ativo} onChange={(e) => setF({ ...f, ativo: e.target.checked })} />}
          label="Ativo" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={salvar} disabled={!f.nome.trim() || !f.url.trim()}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

const Entregas = ({ w, onClose }) => {
  const [lista, setLista] = useState(null);
  useEffect(() => {
    if (w) suporteApi.get(`/api/integracoes/webhooks/${w.id}/entregas`).then((r) => setLista(r.data)).catch((e) => toast.error(mensagemErro(e)));
  }, [w]);
  if (!w) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Últimas entregas · ${w.nome}`}</DialogTitle>
      <DialogContent>
        {!lista ? "Carregando…" : !lista.length ? <Typography color="textSecondary">Nenhuma entrega ainda.</Typography> : (
          <Table size="small">
            <TableHead><TableRow><TableCell>Quando</TableCell><TableCell>Evento</TableCell><TableCell>Card</TableCell>
              <TableCell>Resposta</TableCell><TableCell>Tentativas</TableCell><TableCell>Tempo</TableCell></TableRow></TableHead>
            <TableBody>
              {lista.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{dataBR(e.criado_em)}</TableCell>
                  <TableCell style={mono}>{e.evento}</TableCell>
                  <TableCell>{e.atendimento_id ? `#${e.atendimento_id}` : "—"}</TableCell>
                  <TableCell style={{ color: okStatus(e.status_http) ? "#2e7d32" : "#c62828" }}>
                    {e.status_http || "sem resposta"}{e.erro ? ` · ${e.erro}` : ""}
                  </TableCell>
                  <TableCell>{e.tentativas}</TableCell>
                  <TableCell>{`${e.duracao_ms} ms`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

const Documentacao = ({ base, eventos }) => (
  <Accordion variant="outlined" style={{ marginTop: 12 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1"><b>Resumo da integração</b></Typography></AccordionSummary>
    <AccordionDetails style={{ display: "block" }}>
      <Typography variant="body2" style={{ marginBottom: 8 }}>
        Resumo rápido. O <a href={MANUAL} target="_blank" rel="noopener noreferrer">manual completo</a> traz o JSON de cada evento
        (cards e formulários), todos os objetos, os 9 endpoints da API e exemplos de receptor em Node.js, Python e PHP.
      </Typography>
      <Typography variant="subtitle2">1. Webhooks — o sistema avisa o seu</Typography>
      <Typography variant="body2" component="div">
        A cada mudança num card, o sistema faz um <b>POST</b> em JSON para cada webhook ativo que assina o evento, em poucos segundos.
        Vale para cards abertos pelo painel, WhatsApp, formulário ou ticket. Responda com qualquer código <b>2xx</b> em até 10 s;
        senão são feitas mais 2 tentativas (após 10 s e 60 s). Erros 4xx (exceto 408/429) não são repetidos.
      </Typography>
      <Typography variant="body2" style={{ marginTop: 8 }}><b>Eventos</b></Typography>
      <ul style={{ margin: "4px 0" }}>
        {Object.entries(eventos).map(([ev, desc]) => <li key={ev}><span style={mono}>{ev}</span>{` — ${desc}`}</li>)}
        <li><span style={mono}>teste</span> — enviado pelo botão "Testar"</li>
      </ul>
      <Typography variant="body2"><b>Cabeçalhos</b></Typography>
      <div style={bloco}>{`Content-Type: application/json; charset=utf-8
User-Agent: Allarmi-Webhook/1.0
X-Allarmi-Evento: card.finalizado
X-Allarmi-Entrega: evt_1234          (único por evento; use para ignorar repetidos)
X-Allarmi-Assinatura: sha256=<HMAC-SHA256 do corpo com o segredo do webhook>`}</div>
      <Typography variant="body2"><b>Corpo (exemplo)</b></Typography>
      <div style={bloco}>{`{
  "evento": "card.finalizado",
  "entrega": "evt_1234",
  "enviado_em": "2026-10-09T15:42:00",
  "card": {
    "id": 185, "status": "FECHADO", "situacao": "Concluído", "categoria": null, "fila_id": null,
    "os": "061412/26", "conta": "C211", "filial": "DSP L2211 PERNAMBUES", "grupo": "DPSP", "cliente": "DPSP",
    "tipo": "MANUTENÇÃO", "sistema": "CFTV", "qra": 396, "tecnico": "JEFERSON VIEIRA", "status_tecnico": "ATIVO",
    "analista": "MURILO", "analista_fim": "ELIAS", "inicio": "2026-10-08T07:53:00", "fim": "2026-10-08T09:27:00",
    "status_os": "FIM DE TESTE", "causa": "MAU CONTATO / CONEXÃO", "protocolo": "V-8EZS1-...",
    "consulta_protocolo": "https://hub.allarmi.com.br/p/V-8EZS1-...",
    "observacao_inicial": "...", "observacao": "...", "origem": "whatsapp", "whatsapp_numero": "5591991003461",
    "continuacao_de": null, "campos_extras": {}, "criado_em": "...", "atualizado_em": "..."
  },
  "andamento": { "id": 991, "autor": "QRA 396 (WHATSAPP)", "texto": "...", "origem": "whatsapp", "criado_em": "..." }
}`}</div>
      <Typography variant="caption" color="textSecondary" component="div">
        "andamento" só vem no evento card.andamento. Datas no horário de Brasília (sem fuso). "card" é sempre o estado atual do card.
      </Typography>
      <Typography variant="body2" style={{ marginTop: 8 }}><b>Conferir a assinatura</b> (recuse se não bater)</Typography>
      <div style={bloco}>{`// Node.js
const crypto = require("crypto");
const esperado = "sha256=" + crypto.createHmac("sha256", SEGREDO).update(corpoBruto).digest("hex");
const ok = crypto.timingSafeEqual(Buffer.from(esperado), Buffer.from(req.headers["x-allarmi-assinatura"]));

# Python
import hmac, hashlib
esperado = "sha256=" + hmac.new(SEGREDO.encode(), corpo_bruto, hashlib.sha256).hexdigest()
ok = hmac.compare_digest(esperado, request.headers["X-Allarmi-Assinatura"])`}</div>
      <Typography variant="subtitle2" style={{ marginTop: 14 }}>2. API — o seu sistema consulta</Typography>
      <Typography variant="body2" component="div">
        Crie uma chave em "Chaves de API" e envie no cabeçalho <span style={mono}>Authorization: Bearer alm_…</span>. Só leitura,
        só cards do suporte.
      </Typography>
      <div style={bloco}>{`GET ${base}/cards?status=FECHADO&desde=2026-10-01T00:00:00&pagina=1&limite=100
    status: ABERTO | PENDENTE | FECHADO   desde: alterados a partir de   os / conta: filtros
    → { "total": 230, "pagina": 1, "limite": 100, "cards": [ { ...mesmo formato do webhook... } ] }

GET ${base}/cards/185
    → { ...card..., "andamentos": [ { "id", "autor", "texto", "origem", "criado_em" } ] }

curl -H "Authorization: Bearer alm_SUA_CHAVE" "${base}/cards?status=ABERTO"`}</div>
      <Typography variant="caption" color="textSecondary" component="div">
        Respostas: 200 ok · 401 chave inválida ou revogada · 404 card não encontrado. Limite por página: 500.
      </Typography>
    </AccordionDetails>
  </Accordion>
);

const Integracoes = () => {
  const [d, setD] = useState(null);
  const [editando, setEditando] = useState(null);
  const [entregas, setEntregas] = useState(null);
  const [segredo, setSegredo] = useState(null); // { titulo, valor, aviso }
  const [nomeChave, setNomeChave] = useState("");
  const [escopos, setEscopos] = useState([]);

  const carregar = useCallback(() => {
    suporteApi.get("/api/integracoes").then((r) => setD(r.data)).catch((e) => toast.error(mensagemErro(e)));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const acao = async (fn) => {
    try {
      await fn();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };
  if (!d) return null;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginTop: 24, marginBottom: 6 }}>
        <Typography variant="body2" style={{ flex: 1 }}>Integrações · Webhooks e API dos cards e formulários</Typography>
        <Button size="small" variant="outlined" color="primary" startIcon={<MenuBookIcon />} component="a" target="_blank" rel="noopener noreferrer"
          href={MANUAL}>Manual completo da integração</Button>
      </div>
      <Paper style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <Typography variant="subtitle1" style={{ flex: 1 }}><b>Webhooks</b> <Typography variant="caption" color="textSecondary">avisam outros sistemas a cada mudança nos cards</Typography></Typography>
          <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setEditando({})}>Adicionar webhook</Button>
        </div>
        {!d.webhooks.length ? <Typography variant="body2" color="textSecondary">Nenhum webhook cadastrado.</Typography> : (
          <Table size="small">
            <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>URL</TableCell><TableCell>Eventos</TableCell>
              <TableCell>Último envio</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
            <TableBody>
              {d.webhooks.map((w) => (
                <TableRow key={w.id} style={{ opacity: w.ativo ? 1 : 0.5 }}>
                  <TableCell><b>{w.nome}</b>{!w.ativo && " (inativo)"}</TableCell>
                  <TableCell style={{ ...mono, wordBreak: "break-all", maxWidth: 260 }}>{w.url}</TableCell>
                  <TableCell>{w.eventos.length ? w.eventos.map((e) => <Chip key={e} size="small" label={e} style={{ margin: 2 }} />) : "todos"}</TableCell>
                  <TableCell><Status w={w} /></TableCell>
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Testar (envia um evento 'teste' agora)">
                      <IconButton size="small" onClick={() => acao(async () => {
                        const { data } = await suporteApi.post(`/api/integracoes/webhooks/${w.id}/teste`);
                        (okStatus(data.status_http) ? toast.success : toast.error)(
                          `${w.nome}: ${data.status_http || "sem resposta"} em ${data.duracao_ms} ms${data.erro ? ` · ${data.erro}` : ""}`);
                        carregar();
                      })}><SendIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Últimas entregas"><IconButton size="small" onClick={() => setEntregas(w)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Gerar novo segredo"><IconButton size="small" onClick={() => acao(async () => {
                      if (!window.confirm(`Gerar novo segredo para "${w.nome}"? O atual deixa de valer na hora.`)) return;
                      const { data } = await suporteApi.post(`/api/integracoes/webhooks/${w.id}/segredo`);
                      setSegredo({ titulo: "Novo segredo do webhook", valor: data.segredo, aviso: "Atualize no sistema que recebe. Ele não será mostrado de novo." });
                      carregar();
                    })}><span style={{ fontSize: 13, fontWeight: 700 }}>🔑</span></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => setEditando(w)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => acao(async () => {
                      if (!window.confirm(`Excluir o webhook "${w.nome}"?`)) return;
                      await suporteApi.delete(`/api/integracoes/webhooks/${w.id}`);
                      carregar();
                    })}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper style={{ padding: 16 }}>
        <Typography variant="subtitle1"><b>Chaves de API</b> <Typography variant="caption" color="textSecondary">para outro sistema consultar os cards ({d.base_api})</Typography></Typography>
        <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
          <TextField id="chave-nome" size="small" variant="outlined" placeholder="Nome (ex.: Power BI)" value={nomeChave} onChange={(e) => setNomeChave(e.target.value)} />
          {Object.entries(d.escopos || {}).map(([e, desc]) => (
            <Tooltip key={e} title={desc}>
              <FormControlLabel control={<Checkbox size="small" color="primary" checked={escopos.includes(e)}
                onChange={(ev) => setEscopos(ev.target.checked ? [...escopos, e] : escopos.filter((x) => x !== e))} />} label={e} />
            </Tooltip>
          ))}
          <Button size="small" variant="outlined" color="primary" disabled={nomeChave.trim().length < 2} onClick={() => acao(async () => {
            const { data } = await suporteApi.post("/api/integracoes/chaves", { nome: nomeChave, escopos });
            setSegredo({ titulo: `Chave de API · ${data.nome}`, valor: data.chave, aviso: "Copie e guarde agora: por segurança ela não será mostrada de novo." });
            setNomeChave("");
            setEscopos([]);
            carregar();
          })}>Criar chave</Button>
        </div>
        <Typography variant="caption" color="textSecondary" component="div">
          Sem escopo = consulta de cards e formulários (como antes). <b>relatorios</b> = API de relatórios para BI/Asana. <b>bot</b> = chatbot (Typebot).
        </Typography>
        {d.chaves.map((k) => (
          <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", borderTop: "1px solid rgba(0,0,0,.08)" }}>
            <b>{k.nome}</b><span style={mono}>{`${k.prefixo}…`}</span>
            {(k.escopos || []).map((e) => <Chip key={e} size="small" label={e} />)}
            <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
              {`criada por ${k.criado_por || "-"} em ${dataBR(k.criado_em)} · ${k.ultimo_uso ? `último uso ${dataBR(k.ultimo_uso)}` : "nunca usada"}`}
            </Typography>
            <Button size="small" onClick={() => acao(async () => {
              if (!window.confirm(`Revogar a chave "${k.nome}"? Quem usa ela perde o acesso na hora.`)) return;
              await suporteApi.delete(`/api/integracoes/chaves/${k.id}`);
              carregar();
            })}>Revogar</Button>
          </div>
        ))}
      </Paper>

      <IntegracoesSaida />

      <Documentacao base={d.base_api} eventos={d.eventos} />

      <EditorWebhook w={editando} eventos={d.eventos} onClose={() => setEditando(null)}
        onSalvo={(w, novo) => {
          setEditando(null);
          carregar();
          if (novo) setSegredo({ titulo: `Webhook criado · ${w.nome}`, valor: w.segredo, aviso: "Este é o segredo para conferir a assinatura (X-Allarmi-Assinatura). Guarde: ele não será mostrado de novo." });
          else toast.success("Webhook salvo");
        }} />
      <Entregas w={entregas} onClose={() => setEntregas(null)} />
      <SegredoMostrado {...(segredo || {})} onClose={() => setSegredo(null)} />
    </div>
  );
};

export default Integracoes;
